import { Icon, Typography, Link, Card } from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import { useSubscription, usePlan } from 'commons/useSubscription'
import { PlanId, hasPlanEnded } from 'commons/plan'
import { Routes } from 'app/services/routeService/routeService'
import * as R from 'ramda'
import moment from 'moment'
import React from 'react'
import { FormattedMessage } from 'react-intl'
import { Link as RouterLink } from 'react-router-dom'
import { useIsAuthorized } from 'app/auth/authHooks'
import { useAppName } from 'commons/usePartner'
import { useSelector } from 'react-redux'
import { path } from 'ramda'

export const useWarningStyle = makeStyles((theme) => ({
  warningBox: {
    color: '#9D5425',
    backgroundColor: 'rgba(251, 191, 36, 0.15)',
  },
  warningContent: {
    color: `#9D5425 !important`,
  },
  warningIcon: {
    fontSize: '2.2rem',
    color: '#ffffff !important',
  },
  iconArea: {
    backgroundColor: 'rgba(251, 191, 36, 1)',
  },
}))

export const useWarningStylePayment = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.negative.background,
    color: theme.palette.darkGrey.main,
  },
  downgradedWarning: {
    backgroundColor: theme.palette.secondary.intensive,
    color: theme.palette.secondary.main,
  },
  icon: {
    fontSize: '3.2rem',
    color: theme.palette.negative.main,
    marginRight: '5px',
  },
  downgradedIcon: {
    fontSize: '3.2rem',
    color: theme.palette.secondary.main,
    marginRight: '5px',
  },
}))

export const diffDayBetween = ({ start, end }) => {
  return moment(end).diff(moment(start), 'days')
}

const MINIMUM_START_WARNING_DAYS = 7

const isNotesAvailableBySubscription = R.compose(
  R.propSatisfies(R.gt(R.__, 0), 'notesAvailable'),
  R.propOr({}, 'info'),
)

function WarningEndPlan(props) {
  const isCurrentPathRedemptionPath = R.compose(
    R.includes(Routes.redemptionBase()),
    R.defaultTo(''),
    R.path(['location', 'pathname']),
  )(props)

  const pendingApproval = useSelector(
    path(['place', 'info', 'pendingApproval']),
  )

  const subscription = useSubscription()
  const plan = usePlan()
  const appName = useAppName()
  const classes = useWarningStyle()
  const toChangePlan = Routes.billingchangePlan()

  const diffDaysBetweenTodayAndEndPlan = diffDayBetween({
    start: moment(),
    end: R.propOr(null, 'endsAt', subscription),
  })
  const isAuthorized = useIsAuthorized()

  const isNotesAvailable = isNotesAvailableBySubscription(subscription)

  const isPeriodShowWarning =
    diffDaysBetweenTodayAndEndPlan <= MINIMUM_START_WARNING_DAYS

  if (
    !isAuthorized ||
    !R.equals(PlanId.FREE_TRIAL, plan && plan.id) ||
    isCurrentPathRedemptionPath ||
    hasPlanEnded({ subscription }) ||
    pendingApproval
  ) {
    return null
  }

  if (!isNotesAvailable) {
    return (
      <WarningContainer icon="warning">
        <FormattedMessage
          id="warningEndTrial.notesAvailable.label"
          values={{
            numberNotifications: R.prop('notesQuantity', plan),
            b: (text) => <b>{text}</b>,
            l1: (text) => (
              <Link
                to={toChangePlan}
                component={RouterLink}
                className={clsx(classes.warningContent, 'font-bold underline')}
              >
                {text}
              </Link>
            ),
            newLine: <br />,
          }}
        />
      </WarningContainer>
    )
  }

  if (isPeriodShowWarning) {
    return (
      <WarningContainer icon="warning">
        <FormattedMessage
          id="warningEndTrial.remain.label"
          values={{
            remainDays: diffDaysBetweenTodayAndEndPlan,
            b: (text) => <b>{text}</b>,
            l1: (text) => (
              <Link
                to={toChangePlan}
                component={RouterLink}
                className={clsx(classes.warningContent, 'font-bold underline')}
              >
                {text}
              </Link>
            ),
            newLine: <br />,
            appName,
          }}
        />
      </WarningContainer>
    )
  }

  return null
}

export const WarningContainer = ({ children, icon }) => {
  const classes = useWarningStyle()
  return (
    <div className="w-full">
      <div
        className={clsx(
          classes.warningBox,
          'flex items-start sm:h-60 h-96 rounded',
        )}
      >
        <div className={clsx('flex m-auto mx-5 rounded', classes.iconArea)}>
          <Icon
            className={clsx(
              'm-4 rounded',
              classes.warningIcon,
              classes.warningContent,
            )}
          >
            {icon}
          </Icon>
        </div>
        <Typography className={clsx(classes.warningContent, 'my-auto')}>
          {children}
        </Typography>
      </div>
    </div>
  )
}

export const BillingWarningContainer = ({ children, icon }) => {
  const classes = useWarningStylePayment()
  return (
    <Card className={clsx('flex p-5', classes.root)}>
      <Icon className={clsx('mx-12', classes.icon)}>{icon}</Icon>
      <Typography className="pt-3">{children}</Typography>
    </Card>
  )
}

export const BillingDowngradedWarningContainer = ({ children, icon }) => {
  const classes = useWarningStylePayment()
  return (
    <Card className={clsx('flex p-5', classes.downgradedWarning)}>
      <Icon className={clsx('mx-12', classes.downgradedIcon)}>{icon}</Icon>
      <Typography className="pt-3">{children}</Typography>
    </Card>
  )
}

export default WarningEndPlan
